import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize, timeout } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { AuthService } from '../../../../auth/services/auth.service';
import { AlertaService } from '../../../../core/services/alerta.service';
import { StorageService } from '../../../../core/services/storage.service';
import { Usuario } from '../../../../core/models/usuario';

interface Perfil {
    nombres: string;
    usuario: string;
    tipoUsuario: string;
    fechaCreacion?: string;
}

@Component({
    selector: 'app-perfil',
    templateUrl: './perfil.component.html',
    styleUrl: './perfil.component.scss',
    imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule]
})
export class PerfilComponent implements OnInit {
    cargando = true;
    actualizando = false;
    guardando = false;
    perfil: Perfil = { nombres: '', usuario: '', tipoUsuario: '' };
    formularioPerfil: Perfil = { nombres: '', usuario: '', tipoUsuario: '' };
    formularioClave = { passwordActual: '', passwordNuevo: '', confirmarClave: '' };
    mostrarClaveActual = false;
    mostrarClaveNueva = false;
    editarVisible = false;
    claveVisible = false;

    constructor(
        private readonly authService: AuthService,
        private readonly storageService: StorageService,
        private readonly alerta: AlertaService,
        private readonly router: Router,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.cargarPerfil(false);
    }

    cargarPerfil(actualizar = true): void {
        if (actualizar) {
            this.actualizando = true;
        } else {
            this.cargando = true;
        }
        this.authService.obtenerPerfil().pipe(
            timeout(15000),
            finalize(() => this.finalizarCargaPerfil()),
        ).subscribe({
            next: (respuesta) => {
                if (!respuesta.isSuccess || !respuesta.value) {
                    this.alerta.openAlerta('Perfil', respuesta.message || 'No se pudo cargar tu perfil', 'error');
                    this.finalizarCargaPerfil();
                    return;
                }
                this.perfil = respuesta.value as Perfil;
                this.finalizarCargaPerfil();
            },
            error: () => {
                this.cargarPerfilLocal();
                this.alerta.openAlerta('Perfil', 'No se pudo cargar tu perfil', 'error');
                this.finalizarCargaPerfil();
            },
        });
    }

    abrirEditar(): void {
        this.formularioPerfil = { ...this.perfil };
        this.editarVisible = true;
    }

    guardarPerfil(): void {
        if (!this.formularioPerfil.nombres.trim()) {
            this.alerta.openAlerta('Perfil', 'El nombre es obligatorio', 'warn');
            return;
        }
        this.guardando = true;
        this.authService.actualizarPerfil({ nombres: this.formularioPerfil.nombres }).subscribe({
            next: (respuesta) => {
                this.guardando = false;
                if (!respuesta.isSuccess || !respuesta.value) {
                    this.alerta.openAlerta('Perfil', respuesta.message || 'No se pudo actualizar el perfil', 'error');
                    return;
                }
                this.perfil = { ...this.perfil, ...(respuesta.value as Partial<Perfil>) };
                this.actualizarUsuarioLocal();
                this.editarVisible = false;
                this.alerta.openAlerta('Perfil actualizado', respuesta.message, 'success');
            },
            error: () => { this.guardando = false; this.alerta.openAlerta('Perfil', 'No se pudo actualizar el perfil', 'error'); },
        });
    }

    abrirCambioClave(): void {
        this.formularioClave = { passwordActual: '', passwordNuevo: '', confirmarClave: '' };
        this.mostrarClaveActual = false;
        this.mostrarClaveNueva = false;
        this.claveVisible = true;
    }

    cambiarClave(): void {
        if (!this.formularioClave.passwordActual || !this.formularioClave.passwordNuevo) {
            this.alerta.openAlerta('Contraseña', 'Completa todos los campos', 'warn');
            return;
        }
        if (this.formularioClave.passwordNuevo !== this.formularioClave.confirmarClave) {
            this.alerta.openAlerta('Contraseña', 'La confirmación no coincide con la nueva contraseña', 'warn');
            return;
        }
        this.guardando = true;
        this.authService.cambiarClavePerfil({ passwordActual: this.formularioClave.passwordActual, passwordNuevo: this.formularioClave.passwordNuevo }).subscribe({
            next: (respuesta) => {
                this.guardando = false;
                if (!respuesta.isSuccess) {
                    this.alerta.openAlerta('Contraseña', respuesta.message, 'error');
                    return;
                }
                this.claveVisible = false;
                this.alerta.openAlerta('Contraseña actualizada', respuesta.message, 'success');
                this.storageService.storageClear();
                setTimeout(() => this.router.navigate(['/auth/login']), 900);
            },
            error: () => { this.guardando = false; this.alerta.openAlerta('Contraseña', 'No se pudo actualizar la contraseña', 'error'); },
        });
    }

    fechaRegistro(): string {
        if (!this.perfil.fechaCreacion) return '-';
        return new Intl.DateTimeFormat('es-PE', { dateStyle: 'long' }).format(new Date(this.perfil.fechaCreacion));
    }

    iniciales(): string {
        return this.perfil.nombres.split(' ').filter(Boolean).slice(0, 2).map((nombre) => nombre[0]).join('').toUpperCase() || 'P';
    }

    private actualizarUsuarioLocal(): void {
        const usuario = this.storageService.storageGet<Usuario>('usuario');
        if (!usuario) return;
        this.storageService.storageSet('usuario', { ...usuario, nombreCompleto: this.perfil.nombres, nombreUsuario: this.perfil.usuario });
    }

    private cargarPerfilLocal(): void {
        const usuario = this.storageService.storageGet<Usuario>('usuario');
        if (!usuario) return;
        this.perfil = {
            nombres: usuario.nombreCompleto ?? '',
            usuario: usuario.nombreUsuario ?? '',
            tipoUsuario: usuario.tipoUsuario ?? '',
        };
    }

    private finalizarCargaPerfil(): void {
        this.cargando = false;
        this.actualizando = false;
        this.cdr.detectChanges();
    }
}
